// src/api/belief-view.ts
//
// The Deep Signal belief page (backend: app/services/belief_view.py). Where one
// belief surfaces across a client's series: header, a banded per-question
// table, and by-lane / by-persona tables, all from stored score rows.
//
// Counts (answers, present, ...) are only in an ADMIN's payload; the server
// strips them for clients, so every count field below is optional.

import { apiFetch } from "./api-fetch";
import { downloadExportFile } from "./onboarding-api";
import type { RunCompleteness } from "./run-completeness";
import type { SeriesRebaseline } from "./series-rebaseline";

export type BeliefCellStatus = "measured" | "not_in_battery" | "belief_not_measured" | "no_answers";

export interface BeliefCell {
  run_id: number;
  status: BeliefCellStatus;
  share?: number | null;
  primary_when_present?: number | null;
  primary_when_absent?: number | null;
  /** Fewer than 20 answers behind `share`: show it, never call it a trend. */
  directional?: boolean;
  primary_directional?: boolean;
  primary_absent_directional?: boolean;
  // Admin only.
  answers?: number;
  present?: number;
  primary_present?: number;
  absent?: number;
  primary_absent?: number;
}

export interface BeliefQuote {
  quote: string;
  model: string;
  model_label: string;
  score: number | null;
  primary_rec: boolean;
}

export type BeliefBand = "asks" | "unprompted" | "earlier_only" | "never";

export interface BeliefQuestion {
  query_id: string;
  text: string;
  persona: string;
  question_type: string | null;
  asks_for_belief: boolean;
  /** "framework": the question's variable_ids decide; "admin_flag": the per-question flag. */
  asks_source: "framework" | "admin_flag";
  in_latest_battery: boolean;
  band: BeliefBand;
  marker: {
    kind: "edited_at_rebaseline";
    version: number;
    of_versions: number;
    position: "before" | "after";
    rebaseline_id: number | null;
    applied_at: string | null;
  } | null;
  latest: BeliefCell;
  series: BeliefCell[];
  ever_present: boolean;
  top_quote: BeliefQuote | null;
  quotes: BeliefQuote[];
}

export interface BeliefRead {
  run_id: number;
  read_number: number;
  label: string;
  run_sequence_number: number | null;
  created_at: string | null;
  segment: number;
  battery_version: number;
  belief_measured: boolean;
  completeness?: RunCompleteness | null;
}

export interface BeliefView {
  belief: {
    id: string;
    name: string;
    summary: string;
    claim: string;
    rubric: Partial<Record<"score_2" | "score_1" | "score_0" | "rationale", string>>;
  };
  brand: string;
  reads: BeliefRead[];
  latest_run_id: number;
  header: {
    latest: BeliefCell;
    series: BeliefCell[];
    questions_in_battery: number;
    questions_surfaced: number;
    questions_surfaced_asking: number;
    questions_surfaced_unprompted: number;
    questions_never_surfaced: number;
    questions_asking: number;
  };
  bands: { band: BeliefBand; title: string; questions: BeliefQuestion[] }[];
  by_lane: { lane: string; label: string; in_blend: boolean; latest: BeliefCell; series: BeliefCell[] }[];
  by_persona: { persona: string; latest: BeliefCell; series: BeliefCell[] }[];
  rebaselines: SeriesRebaseline[];
  rules: { trend_min_answers: number };
  viewer_is_admin: boolean;
  admin?: { question_flags: string[] };
}

const base = (customerId: number, beliefId: string) =>
  `/deep-signal/customers/${customerId}/beliefs/${encodeURIComponent(beliefId)}`;

export async function getBeliefView(customerId: number, beliefId: string): Promise<BeliefView> {
  return apiFetch(base(customerId, beliefId));
}

export async function downloadBeliefExport(
  customerId: number,
  beliefId: string,
  format: "md" | "json",
): Promise<void> {
  return downloadExportFile(
    `${base(customerId, beliefId)}/export.${format}`,
    `belief_${customerId}_${beliefId}.${format}`,
    format === "md" ? "text/markdown" : "application/json",
  );
}

/** Admin: the questions written to surface this belief (unmapped questions only). */
export async function saveBeliefQuestionFlags(
  customerId: number,
  beliefId: string,
  queryIds: string[],
): Promise<{ ok: boolean; query_ids: string[] }> {
  return apiFetch(`${base(customerId, beliefId)}/question-flags`, {
    method: "PUT",
    body: JSON.stringify({ query_ids: queryIds }),
  });
}

/** Admin: the belief whose block a read's report includes. */
export async function getReportBelief(
  customerId: number,
  runId: number,
): Promise<{ run_id: number; belief_id: string | null }> {
  return apiFetch(`/deep-signal/customers/${customerId}/runs/${runId}/report-belief`);
}

export async function saveReportBelief(
  customerId: number,
  runId: number,
  beliefId: string | null,
): Promise<{ ok: boolean; belief_id: string | null }> {
  return apiFetch(`/deep-signal/customers/${customerId}/runs/${runId}/report-belief`, {
    method: "PUT",
    body: JSON.stringify({ belief_id: beliefId }),
  });
}
